import Big from 'big.js';
import axios, { AxiosError } from 'smg/axios';
import Batch from 'smg/batch';
import { AddCartItemParams, GetCartID } from 'smg/customer-api/carts';
import { ChildLineItemModelByID, LineItemByID, LineItemModelByID, LineItemTypeModelByID, LineItemTypeSizeModelByID } from 'smg/customer-api/lineitems';
import { Session } from 'smg/customer-api/session';
import { HandleRequestError } from 'smg/errors';
import * as filepath from 'smg/filepath';
import { Escape } from 'smg/html';
import Modal from 'smg/modal';
import * as models from 'smg/models';
import { G } from 'smg/querystring';
import * as strings from 'smg/strings';
import { Clone } from 'smg/template';
import * as toast from 'smg/toast';
import * as uploads from 'smg/uploads';
import { DollarFormat } from 'smg/util';
import { UUID } from 'smg/uuid';
import * as carts from '../carts';
import { Relref } from '../hugo';
import * as lineitems from '../lineitems';
import { PromptQuantityChange } from '../quote';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

// declare interface quantityOverride {
//     cartItemID: string;
//     objectLineItemID: string;
//     quantity: number;
// }

export default async function main() {
    const errorId = document.querySelector('#error-div') as HTMLElement;

    const quoteID = G().Get('quote');
    if (quoteID) {
        await carts.AddCartItem({ quoteID }, {
            promptToCheckout: false,
            hideCartLink: true,
        }).catch(() => errorId.classList.remove('d-none'));
    }
    const orderID = G().Get('order');
    if (orderID) {
        await carts.AddCartItem({ orderID }, {
            promptToCheckout: false,
            hideCartLink: true,
        }).catch(() => errorId.classList.remove('d-none'));
    }
    const newCartItem = G().Get('new');
    if (newCartItem) {
        try {
            await carts.AddCartItem(JSON.parse(newCartItem) as AddCartItemParams, {
                promptToCheckout: false,
            });
        } catch (e) {
            HandleRequestError(e);
        }
    }
    const cartID = G().Get('cart');
    if (cartID) {
        await carts.AddCartItem({ cartID }, {
            promptToCheckout: false,
            hideCartLink: true,
        }).catch(() => errorId.classList.remove('d-none'));
    }

    // const lineItemPriceBreakClickedIDs: string[] = [];

    const cartCard = document.querySelector('#cart-card') as HTMLElement;

    const spinner = document.querySelector('#cart-spinner') as HTMLElement;
    const checkoutButton = document.querySelector('#checkout-button') as HTMLElement;

    const cartEmpty = document.querySelector('#cart-empty') as HTMLElement;
    cartEmpty.parentElement?.removeChild(cartEmpty);
    cartEmpty.classList.remove('d-none');

    const showEmptyCart = () => {
        cartCard.innerHTML = '';
        cartCard.appendChild(cartEmpty);

        const quoteSearchBtn = document.querySelector('#quote-search-btn') as HTMLAnchorElement;
        quoteSearchBtn.classList.add('btn', 'btn-primary', 'text-white');
        quoteSearchBtn.parentElement?.classList.remove('justify-content-start');
        quoteSearchBtn.parentElement?.classList.add('justify-content-center');
        quoteSearchBtn.innerHTML = '<i class="fas fa-search"></i> Search for your quotes.';

        checkoutButton.setAttribute('disabled', 'disabled');
    };

    const $cartItemTemplate = document.querySelector('#cart-item-template') as HTMLTemplateElement;
    const $cartItemStockItemTemplate = document.querySelector('#cart-item-stock-item-template') as HTMLTemplateElement;
    const $cartItemUploadTemplate = document.querySelector('#cart-item-upload-template') as HTMLTemplateElement;
    const drawCartItem = async (cartItem: models.CartItem): Promise<{ $: HTMLElement; p?: Promise<unknown> }> => {
        let $template = $cartItemTemplate;
        if (cartItem.stockItem) {
            $template = $cartItemStockItemTemplate;
        } else if (cartItem.upload) {
            $template = $cartItemUploadTemplate;
        }

        const $cartItem = Clone($template).firstElementChild as HTMLElement;
        $cartItem.removeAttribute('id');
        const promises: Promise<unknown>[] = [];
        const title = $cartItem.querySelector('.cart-item-title') as HTMLElement;

        const subtotal = $cartItem.querySelector('.cart-item-subtotal') as HTMLElement;
        subtotal.textContent = DollarFormat(cartItem.subtotal || 0);

        const oldSubtotal = $cartItem.querySelector('.text-decoration-line-through') as HTMLElement;
        if (cartItem.maxNetworkSubtotal && Big(cartItem.maxNetworkSubtotal).gt(cartItem.subtotal || 0)) {
            subtotal.classList.add('text-primary');
            oldSubtotal.textContent = DollarFormat(cartItem.maxNetworkSubtotal);
        } else {
            oldSubtotal.parentElement?.removeChild(oldSubtotal);
        }

        const deleteLink = $cartItem.querySelector('.cart-item-delete') as HTMLAnchorElement;
        deleteLink.addEventListener('click', e => {
            e.preventDefault();

            let hr: HTMLElement | undefined;
            if ($cartItem.nextElementSibling?.nodeName.toLowerCase() === 'hr') {
                hr = $cartItem.nextElementSibling as HTMLElement;
            } else if ($cartItem.previousElementSibling?.nodeName.toLowerCase() === 'hr') {
                hr = $cartItem.previousElementSibling as HTMLElement;
            }

            const els = [$cartItem];
            if (hr) {
                els.push(hr);
            }

            els.forEach(el => el.classList.add('d-none'));
            carts.DeleteCartItem(cartItem.id || '', cartItem.quantity || 1, drawCart).
                then(() => {
                    void drawCart();
                    els.forEach(el => el.parentElement?.removeChild(el));
                    if (!carts.GetCurrentCartCount()) {
                        showEmptyCart();
                    }
                }).
                catch(err => {
                    els.forEach(el => el.classList.remove('d-none'));
                    throw err;
                });
        });

        const revisionParent = $cartItem.querySelector<HTMLElement>('.cart-item-revision-parent');
        const revision = revisionParent?.querySelector<HTMLAnchorElement>('.cart-item-revision');
        if (cartItem.quote?.id && revision) {
            revision.href = `${Relref('/revisions')}?id=${cartItem.quote.key || ''}`;
        } else if (revisionParent) {
            revisionParent.parentElement?.removeChild(revisionParent);
        }

        const info = $cartItem.querySelector<HTMLAnchorElement>('.cart-item-info');
        if (cartItem.quote?.quoteLineItems?.length || cartItem.order?.orderLineItems?.length || cartItem.uploadLineItem) {
            info?.addEventListener('click', async e => {
                e.preventDefault();

                const entityLineItem: (models.QuoteLineItem | models.OrderLineItem)[] = [];
                if (cartItem.quote?.quoteLineItems?.length) {
                    entityLineItem.push(...cartItem.quote.quoteLineItems);
                } else if (cartItem.order?.orderLineItems?.length) {
                    entityLineItem.push(...cartItem.order.orderLineItems);
                } else if (cartItem.uploadLineItem) {
                    entityLineItem.push(...uploads.CartItemOrderLineItems(cartItem));
                }

                new Modal({
                    title: `Pricing breakdown for ${title.textContent || ''}`,
                    body: await lineitems.Table(entityLineItem),
                    size: 'modal-lg',
                    cancelText: 'Close',
                });
            });
        } else if (info) {
            info.parentElement?.removeChild(info);
        }
        const drawCartItemLine = async (lineItem: models.QuoteLineItem | models.OrderLineItem): Promise<{ $: HTMLElement; p?: Promise<void> }> => {
            lineItem.lineItemModel ??= await LineItemModelByID(lineItem.lineItemID || '');
            lineItem.lineItemType ??= await LineItemTypeModelByID(lineItem.lineItemTypeID || '');
            lineItem.lineItemTypeSize ??= await LineItemTypeSizeModelByID(lineItem.lineItemTypeSizeID || '');
            for (const childLineItem of (lineItem as models.QuoteLineItem).quoteChildLineItems || (lineItem as models.OrderLineItem).orderChildLineItems || []) {
                childLineItem.childLineItem ??= await ChildLineItemModelByID(childLineItem.childLineItemID || '');
                childLineItem.childLineItemOption ??= await LineItemByID(childLineItem.childLineItemOptionID || '');
            }

            const $lineItem = Clone('#cart-item-line', $cartItem).firstElementChild as HTMLElement;
            let p: Promise<void> | undefined;
            const summary = $lineItem.querySelector('.cart-item-summary') as HTMLElement;

            const $proofLink = $lineItem.querySelector('.proof-link') as HTMLAnchorElement;
            const $proofImg = $proofLink.querySelector('img') as HTMLImageElement;
            const proof = (lineItem as models.QuoteLineItem).quoteProof?.proof || (lineItem as models.OrderLineItem).orderProof?.proof;
            let proofName: string = '';
            if (proof?.main) {
                $proofLink.href = proof.main;
                $proofLink.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);

                $proofImg.src = proof.thumbnail || proof.main;

                if ((lineItem as models.QuoteLineItem).quoteProof?.number) {
                    proofName = `Proof ${(lineItem as models.QuoteLineItem).quoteProof?.number || ''}`;
                } else {
                    proofName = (lineItem as models.OrderLineItem).orderProof?.name || '';
                }
                $proofLink.setAttribute('data-caption', proofName);

                $proofImg.title = proofName;
                $proofImg.alt = proofName;
            } else {
                $proofLink.parentElement?.removeChild($proofLink);
            }

            summary.textContent = '';
            if (proofName) {
                if ((lineItem as models.QuoteLineItem).quoteProof?.number) {
                    summary.textContent += `Proof ${(lineItem as models.QuoteLineItem).quoteProof?.number || ''}`;
                } else {
                    summary.textContent += (lineItem as models.OrderLineItem).orderProof?.name ?? '';
                }

                summary.textContent += ' - ';
            }

            const qty = lineItem.quantity || 0;
            const product = (qty === 1 ? lineItem.lineItemModel?.name : lineItem.lineItemModel?.pluralName) || lineItem.lineItem;
            summary.textContent += `${Number(qty).toLocaleString()} ${product || ''}`;

            const controls = $lineItem.querySelector('.cart-item-line-controls') as HTMLElement;
            const changeQty = controls.querySelector('.cart-item-edit-quantity') as HTMLAnchorElement;
            const removeParent = controls.querySelector('.cart-item-line-delete-parent') as HTMLElement;
            const remove = removeParent.querySelector('.cart-item-line-delete') as HTMLAnchorElement;

            if (!lineItem.lineItemModel?.product) {
                controls.parentElement?.removeChild(controls);
            } else {
                if (productCount < 2) {
                    removeParent.parentElement?.removeChild(removeParent);
                }

                removeParent.parentElement?.removeChild(removeParent);

                changeQty.addEventListener('click', e => {
                    e.preventDefault();
                    PromptQuantityChange({ quote: cartItem.quote, order: cartItem.order, lineItem }).then(() => {
                        void drawCart();
                        toast.Success('Request Sent!');
                    }).catch(err => toast.Error(String(err)));
                });

                // if (lineItem.customerEditable) {
                //     changeQty.addEventListener('click', e => {
                //         e.preventDefault();

                //         const body = html.FromString(`
                //         <div class="row">
                //             <div class="col">
                //                 <input id="modal-quantity" class="form-control" type="number" min="0" value="${qty}">
                //             </div>
                //         </div>`);

                //         const quantityInput = body.querySelector('#modal-quantity') as HTMLInputElement;

                //         new Modal({
                //             title: 'Change Quantity',
                //             body,
                //             onSubmit: () =>
                //                 axios.patch(`/cart-items/${cartItem.id || ''}/line-items/${lineItem.id || ''}/quantity`, {
                //                     'quantity': parseInt(quantityInput.value, 10),
                //                 }).then(() => {
                //                     void drawCart();
                //                     toast.Success('Quantity updated.');
                //                 }).catch(err => {
                //                     toast.Error('Something went wrong!');
                //                     throw err;
                //                 }),
                //         });

                //         setTimeout(() => quantityInput.select());
                //     });

                //     if (!lineItemPriceBreakClickedIDs.includes(lineItem.id || '')) {
                //         if (lineItem.lineItemID && lineItem.lineItemTypeID && lineItem.lineItemTypeSizeID && lineItem.quantity) {
                //             let nextBreakPoint = 0;
                //             p = SizeMap().then(resp => {
                //                 if (lineItem.lineItemID && lineItem.lineItemTypeID && lineItem.lineItemTypeSizeID && lineItem.quantity) {
                //                     const sizeMap = resp.get(lineItem.lineItemTypeSizeID);
                //                     const prices = sizeMap?.prices;
                //                     if (prices) {
                //                         for (const qty of prices) {
                //                             if (qty.quantity > lineItem.quantity) {
                //                                 nextBreakPoint = qty.quantity;
                //                                 break;
                //                             }
                //                         }
                //                     }
                //                     if (nextBreakPoint > 0) {
                //                         const qtyOverrideParam: quantityOverride = {
                //                             cartItemID: cartItem.id || '',
                //                             objectLineItemID: lineItem.id || '',
                //                             quantity: nextBreakPoint || 0,
                //                         };
                //                         void b.get<carts.CartResponse>(`/v2/carts/${carts.GetCartID()}`, {
                //                             params: {
                //                                 'quantityOverride': JSON.stringify(qtyOverrideParam),
                //                             },
                //                         }).then(resp => {
                //                             let nextPriceBreakTotal = 0;
                //                             const respData = resp.data.cart;
                //                             if (respData.cartItems) {
                //                                 for (const cartItemIndex of respData.cartItems) {
                //                                     if (cartItem.id === cartItemIndex.id) {
                //                                         if (cartItemIndex.total) {
                //                                             nextPriceBreakTotal = parseInt(cartItemIndex.total, 10);
                //                                         }
                //                                     }
                //                                 }
                //                             }

                //                             const priceDifference = Big(cartItem.total || '0').minus(nextPriceBreakTotal);
                //                             const qtyDifference = Big(nextBreakPoint).minus(Big(lineItem.quantity || '0')).toNumber();
                //                             const qtyDifferenceFormat = new Intl.NumberFormat().format(qtyDifference);
                //                             const priceBreakEl = $lineItem.querySelector('.price-break') as HTMLAnchorElement;

                //                             priceBreakEl.classList.remove('d-none');

                //                             if (priceDifference.lt('0')) {
                //                                 priceBreakEl.textContent = `Add ${qtyDifferenceFormat} more for only ${DollarFormat(priceDifference.abs())}!`;
                //                             } else {
                //                                 priceBreakEl.textContent = `Add ${qtyDifference} more and SAVE ${DollarFormat(priceDifference.abs())}!`;
                //                             }

                //                             priceBreakEl.addEventListener('click', e => {
                //                                 e.preventDefault();

                //                                 priceBreakEl.classList.add('d-none');

                //                                 axios.patch(`/cart-items/${cartItem.id || ''}/line-items/${lineItem.id || ''}/quantity`, {
                //                                     'quantity': nextBreakPoint,
                //                                 }).then(() => {
                //                                     lineItemPriceBreakClickedIDs.push(lineItem.id || '');
                //                                     void drawCart();
                //                                     toast.Success('Quantity updated.');
                //                                 }).catch(err => {
                //                                     HandleRequestError(err);
                //                                 });
                //                             });
                //                         });
                //                     }
                //                 }
                //             });
                //         }
                //     }
                // }

                remove.addEventListener('click', e => {
                    e.preventDefault();

                    const hide = () => $lineItem.classList.add('d-none');
                    const show = () => $lineItem.classList.remove('d-none');

                    const deleteItem = async () => {
                        hide();
                        return axios.delete(`/cart-items/${cartItem.id || ''}/line-items/${lineItem.id || ''}`).
                            then(() => {
                                void drawCart();

                                const undoLinkID = `undo-${UUID()}`;
                                const t = toast.Success(`Removed from cart. <a href="${Relref('/cart')}" class="text-white text-decoration-underline" id="${undoLinkID}">Undo</a>`);

                                const undo = document.querySelector(`#${undoLinkID}`) as HTMLAnchorElement;
                                undo.addEventListener('click', e => {
                                    e.preventDefault();
                                    t?.dispose();
                                    return restore();
                                });
                            }).
                            catch(err => {
                                toast.Error('Something went wrong!');
                                show();
                                throw err;
                            });
                    };

                    const restore = async () => {
                        show();
                        return axios.patch(`/cart-items/${cartItem.id || ''}/line-items/${lineItem.id || ''}/active`, {
                            'active': true,
                        }).then(() => {
                            void drawCart();
                            toast.Success('Restored item.');
                        }).catch(err => {
                            hide();
                            HandleRequestError(err);
                        });
                    };

                    void deleteItem();
                });
            }

            if (lineItem.invalidCustomQuantity) {
                $lineItem.querySelector<HTMLElement>('.cart-item-invalid-quantity')?.classList.remove('d-none');

                const lowQtyRequest = $lineItem.querySelector('.request-low-qty-pricing') as HTMLAnchorElement;
                lowQtyRequest.addEventListener('click', e => {
                    e.preventDefault();

                    PromptQuantityChange({ quote: cartItem.quote, order: cartItem.order, lineItem }).then(() => {
                        void drawCart();
                        toast.Success('Request Sent!');
                    }).catch(err => toast.Error(String(err)));
                });
            }
            return { $: $lineItem, p };
        };

        const $cartItemDescription = $cartItem.querySelector('.cart-item-description') as HTMLElement;

        const proofs: models.Proof[] = [];
        const shownProofs = new Set<string>();

        let productCount = 0;
        const objectLineItems = cartItem.quote?.quoteLineItems || cartItem.order?.orderLineItems;
        if (objectLineItems) {
            for (const objectLineItem of objectLineItems) {
                if (objectLineItem.lineItemModel?.tax || objectLineItem.lineItemModel?.shipping) {
                    continue;
                }

                productCount++;

                if (productCount >= 2) {
                    break;
                }
            }
        }

        let invalidCustomQuantity = false;

        if (cartItem.quote) {
            const sess = await Session().catch(() => undefined);
            if (!(sess?.userID === cartItem.quote.quoteRequest?.userID)) {
                const $balanceContainer = $cartItem.querySelector('#balance-alert-container') as HTMLElement;
                if (cartItem.quote.user?.balance && Big(cartItem.quote.user.balance).gt('0')) {
                    $balanceContainer.classList.remove('d-none');
                }
            }

            title.textContent = `Quote ${cartItem.quote.serial || ''}`;

            if (cartItem.quote.quoteProofs) {
                for (const quoteProof of cartItem.quote.quoteProofs) {
                    if (!quoteProof.proof) {
                        continue;
                    }
                    quoteProof.proof.name = `Proof ${quoteProof.name || ''}`;
                    proofs.push(quoteProof.proof);
                }
            }

            if (cartItem.quote.quoteLineItems) {
                for (const quoteLineItem of cartItem.quote.quoteLineItems) {
                    if (quoteLineItem.lineItemModel?.tax || quoteLineItem.lineItemModel?.shipping) {
                        continue;
                    }
                    const { $, p } = await drawCartItemLine(quoteLineItem);
                    if (p) {
                        promises.push(p);
                    }
                    $cartItemDescription.appendChild($);

                    if (quoteLineItem.quoteProof?.proof?.id) {
                        shownProofs.add(quoteLineItem.quoteProof?.proof.id);
                    }

                    if (quoteLineItem.invalidCustomQuantity) {
                        invalidCustomQuantity = true;
                    }
                }
            }
        } else if (cartItem.order) {
            title.textContent = `Reorder ${cartItem.order.invoiceNumber || ''}`;

            if (cartItem.order.orderProofs) {
                for (const orderProof of cartItem.order.orderProofs) {
                    if (!orderProof.proof) {
                        continue;
                    }
                    orderProof.proof.name = `Proof ${strings.TrimSuffix(orderProof.name || '', filepath.Ext(orderProof.name || ''))}`;
                    proofs.push(orderProof.proof);
                }
            }

            if (cartItem.order.orderLineItems?.length) {
                for (const orderLineItem of cartItem.order.orderLineItems) {
                    if (orderLineItem.lineItemModel?.tax || orderLineItem.lineItemModel?.shipping) {
                        continue;
                    }
                    const { $, p } = await drawCartItemLine(orderLineItem);
                    if (p) {
                        promises.push(p);
                    }
                    $cartItemDescription.appendChild($);

                    if (orderLineItem.orderProof?.proof?.id) {
                        shownProofs.add(orderLineItem.orderProof?.proof.id);
                    }

                    if (orderLineItem.invalidCustomQuantity) {
                        invalidCustomQuantity = true;
                    }
                }
            }
        } else if (cartItem.stockItem) {
            const absoluteURLMatch = new RegExp('^https?://', 'i');
            if (cartItem.stockItem.permalink?.match(absoluteURLMatch)) {
                title.innerHTML = `<a href="${Escape(cartItem.stockItem.permalink)}">${Escape(cartItem.stockItem.name || '')}</a>`;
            } else {
                title.textContent = cartItem.stockItem.name || '';
            }

            if (cartItem.stockItem.stockItemImage) {
                const cartItemImage = $cartItem.querySelector<HTMLElement>('.cart-item-image');
                if (cartItemImage) {
                    cartItemImage.classList.remove('d-none');
                    const a = cartItemImage.querySelector('a');
                    if (a) {
                        a.href = cartItem.stockItem.stockItemImage.main || '#';
                        a.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);
                        a.setAttribute('data-caption', cartItem.stockItem.name || '');
                    }
                    const img = cartItemImage.querySelector('img');
                    if (img) {
                        img.src = cartItem.stockItem.stockItemImage.thumbnail || '';
                        img.alt = cartItem.stockItem.name || '';
                        img.title = cartItem.stockItem.name || '';
                    }
                }
            }

            if (cartItem.customizations && Object.keys(cartItem.customizations).length) {
                for (const [key, value] of Object.entries(cartItem.customizations)) {
                    const customization = Clone('.cart-item-customization-template', $cartItem).firstElementChild as HTMLElement;
                    const keyEl = customization.querySelector<HTMLElement>('.cart-item-customization-key');
                    if (keyEl) {
                        keyEl.textContent = key;
                    }

                    const valueEl = customization.querySelector<HTMLElement>('.cart-item-customization-value');
                    if (valueEl) {
                        valueEl.textContent = value;
                    }

                    $cartItemDescription.appendChild(customization);
                }
            }

            const qtyContainer = $cartItem.querySelector<HTMLElement>('.cart-item-quantity-container');

            const qty = qtyContainer?.querySelector<HTMLElement>('.cart-item-quantity');
            if (qty) {
                qty.textContent = new Intl.NumberFormat().format(cartItem.quantity || 0);
            }

            const qtyEdit = qtyContainer?.querySelector<HTMLAnchorElement>('.cart-item-quantity-edit');
            qtyEdit?.addEventListener('click', async e => {
                e.preventDefault();

                const oldQty = cartItem.quantity || 1;
                const newQty = await carts.PromptQuantityEdit(oldQty || 1, (cartItem.quantityPricing && Object.keys(cartItem.quantityPricing).length ? cartItem.quantityPricing : undefined) || { '1': cartItem.stockItem?.price || '0' });

                axios.patch(`/cart-items/${cartItem.id || ''}`, {
                    quantity: newQty,
                }).then(() => {
                    toast.Success('Quantity updated');
                    void drawCart();
                }).catch(err => {
                    HandleRequestError(err);

                    cartItem.quantity = oldQty;
                    void drawCart();
                });
            });
        } else if (cartItem.upload) {
            const name = cartItem.uploadLineItem?.name || cartItem.upload?.name || 'Custom Design';

            title.textContent = name;

            const orderLineItem = uploads.CartItemOrderLineItems(cartItem)[0];

            const { $, p } = await drawCartItemLine(orderLineItem);
            if (p) {
                promises.push(p);
            }
            $cartItemDescription.appendChild($);

            const $cartItemImage = $cartItem.querySelector<HTMLElement>('.cart-item-image');
            if ($cartItemImage) {
                $cartItemImage.classList.remove('d-none');
                const $a = $cartItemImage.querySelector('a');
                if ($a) {
                    $a.href = `${window.cloudFront}/${cartItem.upload.key ?? ''}`;
                    $a.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);
                    $a.setAttribute('data-caption', 'Custom Design');
                }
                const $img = $cartItemImage.querySelector('img');
                if ($img) {
                    void uploads.Load($img, `${window.cloudFront}/${cartItem.upload.key ?? ''}` || '', name, cartItem.upload.size ?? 0);
                }
            }

            if (cartItem.options?.length) {
                for (const opt of cartItem.options) {
                    const option = Clone('.cart-item-customization-template', $cartItem).firstElementChild as HTMLElement;
                    const $key = option.querySelector<HTMLElement>('.cart-item-customization-key');
                    if ($key) {
                        $key.textContent = opt.name ?? '';
                    }

                    const $value = option.querySelector<HTMLElement>('.cart-item-customization-value');
                    if ($value) {
                        $value.textContent = opt.value ?? '';
                    }

                    $cartItemDescription.appendChild(option);
                }
            }

            const qtyContainer = $cartItem.querySelector<HTMLElement>('.cart-item-quantity-container');

            const qty = qtyContainer?.querySelector<HTMLElement>('.cart-item-quantity');
            if (qty) {
                qty.textContent = new Intl.NumberFormat().format(cartItem.quantity || 0);
            }

            const qtyEdit = qtyContainer?.querySelector<HTMLAnchorElement>('.cart-item-quantity-edit');
            qtyEdit?.addEventListener('click', async e => {
                e.preventDefault();

                const oldQty = cartItem.quantity || 1;
                const quantityPrices: Record<number, string> = {};
                if (cartItem.quantityPricing && Object.keys(cartItem.quantityPricing).length) {
                    for (const [qty, price] of Object.entries(cartItem.quantityPricing)) {
                        quantityPrices[parseInt(qty, 10)] = price;
                    }
                } else {
                    quantityPrices[cartItem.quantity || 1] = Big(cartItem.total ?? '0').div(cartItem.quantity || 0).toString();
                }
                const newQty = await carts.PromptQuantityEdit(oldQty || 1, quantityPrices);

                axios.patch(`/cart-items/${cartItem.id || ''}`, {
                    quantity: newQty,
                }).then(() => {
                    toast.Success('Quantity updated');
                    void drawCart();
                }).catch(err => {
                    HandleRequestError(err);

                    cartItem.quantity = oldQty;
                    void drawCart();
                });
            });
        }

        if (cartItem.quote || cartItem.order) {
            const entityLineItems: (models.QuoteLineItem | models.OrderLineItem)[] = [];
            entityLineItems.push(...cartItem.quote?.quoteLineItems || []);
            entityLineItems.push(...cartItem.order?.orderLineItems || []);
            const productLineItems = entityLineItems?.filter(lineItem => lineItem.lineItemModel?.product);
            if (productLineItems?.length === 1 &&
                productLineItems[0].lineItemModel?.id === models.LineItemID.LINE_ITEMS_CHALLENGE_COIN &&
                productLineItems[0].lineItemType?.id &&
                productLineItems[0].lineItemTypeSize?.id) {
                let proof: models.Proof | undefined;
                let proofName: string;
                if (cartItem.quote) {
                    proof = cartItem.quote?.quoteLineItems?.[0].quoteProof?.proof;
                    proofName = `Proof ${cartItem.quote?.quoteLineItems?.[0].quoteProof?.number || 1}`;
                } else if (cartItem.order) {
                    proof = cartItem.order?.orderLineItems?.[0].orderProof?.proof;
                    proofName = cartItem.order?.orderLineItems?.[0].orderProof?.name ?? '';
                }

                if (proof) {
                    const $bigAddParent = $cartItem.querySelector('.cart-big-coin-add-to-cart-parent') as HTMLElement;
                    const $bigAddButton = $bigAddParent.querySelector('.cart-big-coin-add-to-cart') as HTMLAnchorElement;

                    // $bigAddParent.classList.remove('d-none');
                    $bigAddButton.addEventListener('click', async e => {
                        e.preventDefault();

                        // check if proof is already an upload
                        let uploadID = proof?.uploadID ?? proof?.upload?.id;
                        if (!uploadID) {
                            const newUploadID = await uploads.NewFromURL(proof?.name ?? '', proof?.image?.files?.Original?.Mime ?? proof?.upload?.mime ?? '', proof?.main ?? '').then(files => files[0].uploadID);
                            uploadID = newUploadID;
                        }

                        const childLineItems: (models.QuoteChildLineItem | models.OrderChildLineItem)[] = [];
                        if (cartItem.quote) {
                            childLineItems.push(...cartItem.quote.quoteLineItems?.[0].quoteChildLineItems || []);
                        }
                        if (cartItem.order) {
                            childLineItems.push(...cartItem.order.orderLineItems?.[0].orderChildLineItems || []);
                        }

                        void carts.AddCartItem({
                            uploadID,
                            quantity: 1,
                            uploadLineItem: {
                                name: `${proofName} - 10" Display Coin`,
                                lineItem: productLineItems[0].lineItemModel?.name ?? '',
                                type: productLineItems[0].lineItemType?.name ?? '',
                                size: '10"',
                                childLineItems: childLineItems?.map(childLineItem => ({
                                    child: childLineItem.childLineItem?.name ?? '',
                                    option: childLineItem.childLineItemOption?.name ?? '',
                                })),
                            },
                        }, {
                            promptToCheckout: false,
                            hideCartLink: true,
                        }).then(() => {
                            void drawCart();
                            toast.Success('10" Display Coin added to cart!');
                        }).catch(HandleRequestError);
                    });
                }
            }
        }

        if (invalidCustomQuantity) {
            checkoutButton.setAttribute('disabled', 'disabled');
        } else {
            checkoutButton.removeAttribute('disabled');
        }

        const footerProofs = $cartItem.querySelector('.cart-item-footer-proofs') as HTMLElement;
        let footerHasProofs = false;

        for (const proof of proofs) {
            if (proof.id && shownProofs.has(proof.id)) {
                continue;
            }

            const proofLink = Clone('.cart-item-footer-proof', $cartItem).firstElementChild as HTMLAnchorElement;
            const proofImg = proofLink.querySelector('img') as HTMLImageElement;
            if (proof?.main) {
                proofLink.href = proof.main;
                proofLink.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);
                proofLink.setAttribute('data-caption', proof.name || '');

                proofImg.src = proof.thumbnail || proof.main;

                proofImg.title = proof.name || '';
                proofImg.alt = proof.name || '';
            } else {
                proofLink.parentElement?.removeChild(proofLink);
            }

            footerProofs.appendChild(proofLink);
            footerHasProofs = true;
        }

        if (cartItem.cartItemUploads?.length) {
            const uploadEls: HTMLElement[] = [];
            for (const cartItemUpload of cartItem.cartItemUploads) {
                const upload = cartItemUpload.upload;
                if (!upload) {
                    continue;
                }
                if (upload.name?.indexOf('.') === 0) {
                    continue;
                }

                const a = Clone('.cart-item-footer-proof', $cartItem).firstElementChild as HTMLAnchorElement;
                a.setAttribute('data-name', upload.name || '');
                a.href = `${window.cloudFront}/${upload.key || ''}`;
                a.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);
                a.setAttribute('data-caption', upload.name || '');

                uploadEls.push(a);

                footerProofs.appendChild(a);
                footerHasProofs = true;
            }

            uploads.LoadAll(uploadEls);
        }

        if (!footerHasProofs) {
            footerProofs.parentElement?.removeChild(footerProofs);
        }
        return { $: $cartItem, p: Promise.all(promises) };
    };

    const drawCart = () => {
        checkoutButton.removeAttribute('disabled');
        const subtotal = document.querySelectorAll('.cart-subtotal');

        if (!GetCartID()) {
            showEmptyCart();
            return;
        }

        return carts.GetCart().then(async cart => {
            subtotal.forEach(el => el.textContent = DollarFormat(cart?.total || '0'));

            if (!cart?.cart.cartItems?.length) {
                showEmptyCart();
                return;
            }
            spinner.parentElement?.removeChild(spinner);
            const frag = document.createDocumentFragment();

            const qtyBatchUpdate = new Batch();

            let i = 0;
            const promises: Promise<unknown>[] = [];
            for (const cartItem of cart.cart.cartItems) {
                if (i++) {
                    const hr = document.createElement('hr') as HTMLElement;
                    hr.classList.add('m-0');
                    frag.appendChild(hr);
                }
                const { $, p } = await drawCartItem(cartItem);
                frag.appendChild($);
                if (p) {
                    promises.push(p);
                }
            }
            cartCard.innerHTML = '';
            cartCard.appendChild(frag);
            void Promise.all(promises).then(() => qtyBatchUpdate.Exec());
        }).catch(err => {
            if ((err as AxiosError).response?.status === 404) {
                showEmptyCart();
                return;
            }
            HandleRequestError(err);
        });
    };

    void drawCart();
}